// Copyright 2024, Imprivata, Inc.  All rights reserved.

import { idpGet } from '@api/commonServices';
import { StorageKeys } from '@api/constants';
import type { IdpMetadataInfo, OrgInfoGetResponse } from '@api/types';
import { ErrorCode } from '@errorHandler/constants';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '@errorHandler/errors';
import { featureSupported } from '@/featureFlagsUtils';
import { getSPMetadataURL } from '@utils/utils';
import { Button } from '@imprivata-cloud/components';
import { Alert, Form } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as api from '../../../api/setupServices';
import ConfirmOwnershipModal from '../../../components/otpChallenge/ConfirmOwnershipModal';
import { getSetupContainer } from '../../layout/SetupPageLayout';
import IdpInfoForm, {
  type FormValues,
  IdpFormFieldNames,
} from '../../../components/idpInfoForm/IdpInfoForm';

const TRANSLATION_ROOT = 'setup.external-idp-info';

export const businessEmailDomain = (email: string | undefined): string | null => {
  if (!email) {
    return null;
  }
  const idx = email.lastIndexOf('@');
  if (idx < 0 || idx === email.length - 1) {
    return null;
  }
  return email.slice(idx + 1).toLowerCase();
};

export const verifiedBaselines = (
  configuredDomain: string | undefined,
  orgInfo: OrgInfoGetResponse | undefined,
): ReadonlySet<string> => {
  const set = new Set<string>();
  if (configuredDomain) {
    set.add(configuredDomain.toLowerCase());
  }
  if (orgInfo && api.isBusinessEmailVerified(orgInfo)) {
    const domain = businessEmailDomain(orgInfo.businessEmail);
    if (domain) {
      set.add(domain);
    }
  }
  return set;
};

type DomainChallenge =
  | { kind: 'idle' }
  | { kind: 'challenging'; domain: string; email: string; otpId: string; issuedAtMs: number }
  | { kind: 'deferred'; domain: string; email: string; otpId: string; issuedAtMs: number };

const IDLE: DomainChallenge = { kind: 'idle' };

const ExternalIDPInfo = ({
  setIdpInfoSaved,
  setMetadataId,
  setDidVerifyDomain,
}: {
  setIdpInfoSaved: (idpInfoSaved: boolean) => void;
  setMetadataId: (metadataId: string | null) => void;
  setDidVerifyDomain: (verified: boolean) => void;
}) => {
  const [form] = Form.useForm<FormValues>();
  const { t } = useTranslation();
  const { emitError, clearErrorNotifications } = useErrorContext();
  const [isLoading, setIsLoading] = useState(false);
  const [idpMetadataInfo, setIdpMetadataInfo] = useState<IdpMetadataInfo>();
  const [baselines, setBaselines] = useState<ReadonlySet<string>>(new Set());
  const [baselinesLoaded, setBaselinesLoaded] = useState(false);
  const [challenge, setChallenge] = useState<DomainChallenge>(IDLE);
  const emailVerificationEnabled = featureSupported('EMAIL_VERIFICATION_ENABLED');

  const currentDomain = Form.useWatch(IdpFormFieldNames.DOMAIN, form);
  const normalisedCurrentDomain = (currentDomain ?? '').toLowerCase().trim();
  const requiresAdminEmail =
    baselinesLoaded && !!normalisedCurrentDomain && !baselines.has(normalisedCurrentDomain);

  const isPendingVerification =
    challenge.kind === 'deferred' &&
    challenge.domain === (currentDomain ?? '').toLowerCase().trim();

  const isSubmitBlocked =
    (challenge.kind === 'deferred' || challenge.kind === 'challenging') &&
    challenge.domain === (currentDomain ?? '').toLowerCase().trim();

  const fetchData = useCallback(async () => {
    try {
      clearErrorNotifications();
      setIsLoading(true);
      const [idpResult, orgResult] = await Promise.allSettled([idpGet(true), api.orgInfoGet()]);
      const resp = idpResult.status === 'fulfilled' ? idpResult.value : undefined;
      const orgInfo = orgResult.status === 'fulfilled' ? orgResult.value : undefined;
      if (idpResult.status === 'rejected') {
        throw idpResult.reason;
      }
      const metadata = resp?.idpMetadataInfoList?.[0];
      if (metadata) {
        form.setFieldValue(IdpFormFieldNames.NAME, metadata.idpDisplayName);
        form.setFieldValue(IdpFormFieldNames.URL, metadata.metadataUrl);
        setIdpMetadataInfo(metadata);
      }
      const configuredDomain = resp?.domainNames?.[0];
      setBaselines(verifiedBaselines(configuredDomain, orgInfo));
      setBaselinesLoaded(true);

      if (!form.isFieldTouched(IdpFormFieldNames.DOMAIN)) {
        const prefill =
          configuredDomain ??
          (orgInfo && api.isBusinessEmailVerified(orgInfo)
            ? businessEmailDomain(orgInfo.businessEmail) ?? undefined
            : undefined);
        form.setFieldValue(IdpFormFieldNames.DOMAIN, prefill);
      }
      if (configuredDomain) {
        try {
          sessionStorage.setItem(StorageKeys.SAML_IDP_DOMAIN, configuredDomain);
        } catch {
          /* storage full — sign-in guidance degrades to fallback */
        }
      }
    } catch (e) {
      emitError(new AppError(e as ApiError));
    } finally {
      setIsLoading(false);
    }
  }, [form, emitError, clearErrorNotifications]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  async function validateDomain(domain: string) {
    try {
      const domainRequest = { names: [domain] };
      await api.validateDomain(domainRequest);
      return true;
    } catch (error) {
      const e = error as ApiError;
      if (e?.code === ErrorCode.ALREADY_EXISTS) {
        e.code = ErrorCode.DOMAIN_ALREADY_EXISTS;
      }
      emitError(new AppError(e));
      return false;
    }
  }

  const saveIdpMetadata = async (values: FormValues) => {
    const response = await api.icpMetadataSave(
      {
        metadataId: idpMetadataInfo?.idpMetadataId,
        displayName: values.IdPName,
        domain: values.IdPDomain,
        metadata:
          values.IdPURL || values.IdPXML
            ? { metadataUrl: values.IdPURL || undefined, rawMetadata: values.IdPXML || undefined }
            : undefined,
      },
      true,
    );
    setMetadataId(response.metadataId);
    try {
      sessionStorage.setItem(StorageKeys.SAML_IDP_DOMAIN, values.IdPDomain);
    } catch {
      /* storage full — sign-in guidance degrades to fallback */
    }
    setIdpInfoSaved(true);
  };

  const completeVerifiedDomain = async (domain: string, values: FormValues) => {
    setBaselines(prev => new Set([...prev, domain]));
    setChallenge(IDLE);
    setDidVerifyDomain(true);
    await saveIdpMetadata({ ...values, IdPDomain: domain });
  };

  const finishForm = async (values: FormValues) => {
    try {
      setIsLoading(true);
      const normalisedDomain = values.IdPDomain.toLowerCase().trim();

      const isValid = await validateDomain(normalisedDomain);
      if (!isValid) {
        return;
      }

      if (!emailVerificationEnabled || baselines.has(normalisedDomain)) {
        await saveIdpMetadata({ ...values, IdPDomain: normalisedDomain });
        return;
      }

      const verificationEmail = `${values.AdminEmailLocalPart}@${normalisedDomain}`;
      const { otpId } = await api.requestDomainVerification(normalisedDomain, verificationEmail);
      if (!otpId) {
        await completeVerifiedDomain(normalisedDomain, values);
        return;
      }
      setChallenge({
        kind: 'challenging',
        domain: normalisedDomain,
        email: verificationEmail,
        otpId,
        issuedAtMs: Date.now(),
      });
    } catch (error) {
      emitError(new AppError(error as ApiError));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyAction = async () => {
    if (challenge.kind !== 'deferred') {
      return;
    }
    try {
      setIsLoading(true);
      const { otpId } = await api.requestDomainVerification(challenge.domain, challenge.email);
      if (!otpId) {
        const values = form.getFieldsValue();
        await completeVerifiedDomain(challenge.domain, values);
        return;
      }
      setChallenge({ ...challenge, kind: 'challenging', otpId, issuedAtMs: Date.now() });
    } catch (error) {
      emitError(new AppError(error as ApiError));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerified = async () => {
    if (challenge.kind !== 'challenging') {
      return;
    }
    try {
      setIsLoading(true);
      const values = form.getFieldsValue();
      await completeVerifiedDomain(challenge.domain, values);
    } catch (error) {
      emitError(new AppError(error as ApiError));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelChallenge = () => {
    if (challenge.kind === 'challenging') {
      setChallenge({ ...challenge, kind: 'deferred' });
    }
  };

  const handleResent = (otpId: string, issuedAtMs: number) => {
    if (challenge.kind === 'challenging') {
      setChallenge({ ...challenge, otpId, issuedAtMs });
    }
  };

  const challengeDomain = challenge.kind !== 'idle' ? challenge.domain : null;
  const challengeEmail = challenge.kind !== 'idle' ? challenge.email : null;
  const effects = useMemo(
    () =>
      challengeDomain !== null && challengeEmail !== null
        ? {
            request: async () => {
              const response = await api.requestDomainVerification(challengeDomain, challengeEmail);
              if (!response.otpId) {
                throw new Error('Domain verification resend unexpectedly required no OTP.');
              }
              return { otpId: response.otpId };
            },
            confirm: (otpId: string, code: string) =>
              api.registerDomain(challengeDomain, otpId, code),
          }
        : null,
    [challengeDomain, challengeEmail],
  );

  return (
    <>
      {isPendingVerification && (
        <Alert
          type="info"
          role="status"
          message={t(`${TRANSLATION_ROOT}.content.pending-verification`)}
          description={challenge.domain}
          action={
            <Button
              label={t(`${TRANSLATION_ROOT}.content.verify-domain-action`)}
              onClick={() => void handleVerifyAction()}
            />
          }
          data-testid="pending-verification-alert"
        />
      )}
      <IdpInfoForm
        form={form}
        spMetadataURL={getSPMetadataURL()}
        onFinish={finishForm}
        isLoading={isLoading}
        isSetupSkipAllowed={!!sessionStorage.getItem(StorageKeys.IMPR_IDP_USED) || !!idpMetadataInfo}
        isSubmitBlocked={isSubmitBlocked}
        setupExistingIdpMetadata={idpMetadataInfo}
        onFileReadError={e => emitError(new AppError(e as ApiError))}
        savedDomain={undefined}
        requiresAdminEmail={requiresAdminEmail}
      />
      {challenge.kind === 'challenging' && effects && (
        <ConfirmOwnershipModal
          key={`domain-otp-${challenge.domain}`}
          getContainer={getSetupContainer}
          open
          email={challenge.email}
          otpId={challenge.otpId}
          codeIssuedAtMs={challenge.issuedAtMs}
          effects={effects}
          translationRoot={`${TRANSLATION_ROOT}.domain-otp`}
          onResent={handleResent}
          onVerified={() => void handleVerified()}
          onCancel={handleCancelChallenge}
        />
      )}
    </>
  );
};

export default ExternalIDPInfo;
